import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/layout/Header';
import MobileNav from '@/components/layout/MobileNav';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Loader2, Users, CreditCard, Printer, DollarSign, TrendingUp, AlertCircle, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function StatCard({ icon: Icon, label, value, hint, testid }) {
  return (
    <Card className="p-5 rounded-2xl border-stone-200 bg-white" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-teal-600" />
        </div>
        <div className="min-w-0">
          <div className="text-xs text-stone-500 font-medium uppercase tracking-wide">{label}</div>
          <div className="text-2xl font-semibold text-stone-900 mt-0.5">{value}</div>
          {hint && <div className="text-xs text-stone-500 mt-1">{hint}</div>}
        </div>
      </div>
    </Card>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return format(parseISO(iso), 'MMM d, h:mm a'); } catch { return iso; }
}

function fmtMoney(n) {
  if (n == null || isNaN(n)) return '—';
  return `$${Number(n).toFixed(2)}`;
}

export default function AdminActivityPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [fetching, setFetching] = useState(true);
  const [retrying, setRetrying] = useState(null);
  const [error, setError] = useState(null);

  const load = async (windowDays) => {
    setFetching(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/admin/activity?days=${windowDays}`, { withCredentials: true });
      setData(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not load activity');
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (!user.is_owner) { navigate('/', { replace: true }); return; }
    load(days);
  }, [user, loading, days, navigate]);

  const retryGelato = async (orderId) => {
    setRetrying(orderId);
    try {
      const { data: res } = await axios.post(
        `${API}/admin/print-orders/${orderId}/retry`,
        null,
        { withCredentials: true },
      );
      if (res?.result?.gelato_order_id) {
        toast.success(`Re-submitted to Gelato (id: ${res.result.gelato_order_id})`);
      } else if (res?.result?.error) {
        toast.error(`Still failing: ${res.result.error}`);
      } else {
        toast.success('Re-submitted');
      }
      load(days);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Retry failed');
    } finally {
      setRetrying(null);
    }
  };

  if (loading || (fetching && !data)) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Card className="p-8 text-center text-stone-600">{error}</Card>
        </div>
      </div>
    );
  }

  const t = data?.totals || {};
  const planEntries = Object.entries(data?.plan_breakdown || {});

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-20" data-testid="admin-activity-page">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-semibold text-stone-900">Activity</h1>
            <p className="text-sm text-stone-500 mt-1">Signups, subscriptions, and print orders — last {days} days.</p>
          </div>
          <div className="flex items-center gap-2">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                  d === days
                    ? 'bg-teal-600 text-white'
                    : 'bg-white text-stone-600 border border-stone-200 hover:border-stone-300'
                }`}
                data-testid={`window-${d}d`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        {/* Stat row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <StatCard
            icon={Users}
            label="New signups"
            value={t.signups_in_window ?? 0}
            hint={`${t.total_users ?? 0} total users`}
            testid="stat-signups"
          />
          <StatCard
            icon={CreditCard}
            label="Active subs"
            value={t.active_subscriptions ?? 0}
            hint={planEntries.length ? planEntries.map(([k, v]) => `${v} ${k}`).join(' · ') : '—'}
            testid="stat-subs"
          />
          <StatCard
            icon={Printer}
            label="Paid print orders"
            value={t.paid_print_orders_in_window ?? 0}
            hint={fmtMoney(t.print_revenue_usd)}
            testid="stat-prints"
          />
          <StatCard
            icon={DollarSign}
            label="Revenue (window)"
            value={fmtMoney(t.total_revenue_usd)}
            hint={`Subs ${fmtMoney(t.subscription_revenue_usd)} · Prints ${fmtMoney(t.print_revenue_usd)}`}
            testid="stat-revenue"
          />
        </div>

        {/* Print economics breakdown */}
        {(t.paid_print_orders_in_window ?? 0) > 0 && (
          <Card className="p-4 rounded-2xl border-stone-200 bg-white mb-6" data-testid="print-economics">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-y-2 gap-x-6 text-sm">
              <div>
                <div className="text-xs text-stone-500">Revenue</div>
                <div className="font-semibold text-stone-900">{fmtMoney(t.print_revenue_usd)}</div>
              </div>
              <div>
                <div className="text-xs text-stone-500">Gelato product</div>
                <div className="font-semibold text-stone-900">−{fmtMoney(t.print_gelato_cost_usd)}</div>
              </div>
              <div>
                <div className="text-xs text-stone-500">Gelato shipping</div>
                <div className="font-semibold text-stone-900">−{fmtMoney(t.print_shipping_cost_usd)}</div>
              </div>
              <div>
                <div className="text-xs text-stone-500">Stripe fees</div>
                <div className="font-semibold text-stone-900">−{fmtMoney(t.print_stripe_fee_usd)}</div>
              </div>
              <div>
                <div className="text-xs text-stone-500">Net margin</div>
                <div className={`font-semibold ${(t.print_net_margin_usd || 0) >= 0 ? 'text-teal-700' : 'text-red-700'}`}>
                  {t.print_net_margin_usd == null ? '—' : fmtMoney(t.print_net_margin_usd)}
                </div>
              </div>
            </div>
            {(t.print_orders_with_known_cost ?? 0) < (t.paid_print_orders_in_window ?? 0) && (
              <div className="text-xs text-stone-500 mt-3">
                Margin is calculated on {t.print_orders_with_known_cost} of {t.paid_print_orders_in_window} orders
                (the rest don&apos;t have a wholesale cost set yet).
              </div>
            )}
          </Card>
        )}

        {/* Recent signups */}
        <Card className="p-5 rounded-2xl border-stone-200 bg-white mb-5" data-testid="signups-table">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-teal-600" />
            <h2 className="font-semibold text-stone-900">Recent signups</h2>
          </div>
          {data?.recent_signups?.length ? (
            <div className="divide-y divide-stone-100">
              {data.recent_signups.slice(0, 25).map((u) => (
                <div key={u.id} className="py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-stone-900 truncate">{u.name || '—'}</div>
                    <div className="text-xs text-stone-500 truncate">{u.email}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Badge variant="secondary" className="bg-stone-100 text-stone-600 text-xs">{u.role || 'member'}</Badge>
                    <span className="text-xs text-stone-500 hidden sm:inline">{fmtDate(u.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-stone-500">No signups in this window.</p>
          )}
        </Card>

        {/* Recent subscriptions */}
        <Card className="p-5 rounded-2xl border-stone-200 bg-white mb-5" data-testid="subs-table">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-teal-600" />
            <h2 className="font-semibold text-stone-900">Paid subscriptions</h2>
          </div>
          {data?.recent_subscriptions?.length ? (
            <div className="divide-y divide-stone-100">
              {data.recent_subscriptions.slice(0, 25).map((s, i) => (
                <div key={s.session_id || i} className="py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-stone-900">{s.plan_id || '—'}</div>
                    <div className="text-xs text-stone-500">{s.billing_cycle || '—'}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm font-semibold text-teal-700">
                      {s.amount_total ? `$${(s.amount_total / 100).toFixed(2)}` : '—'}
                    </span>
                    <span className="text-xs text-stone-500 hidden sm:inline">{fmtDate(s.completed_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-stone-500">No paid subscriptions in this window.</p>
          )}
        </Card>

        {/* Recent print orders */}
        <Card className="p-5 rounded-2xl border-stone-200 bg-white" data-testid="orders-table">
          <div className="flex items-center gap-2 mb-3">
            <Printer className="w-4 h-4 text-teal-600" />
            <h2 className="font-semibold text-stone-900">Paid print orders</h2>
          </div>
          {data?.recent_print_orders?.length ? (
            <div className="divide-y divide-stone-100">
              {data.recent_print_orders.slice(0, 25).map((o) => {
                const failed = o.fulfillment_status === 'gelato_failed' || o.fulfillment_status === 'gelato_billing_required';
                const isBilling = o.fulfillment_error_kind === 'billing' || o.fulfillment_status === 'gelato_billing_required';
                return (
                  <div key={o.id} className="py-3" data-testid={`order-row-${o.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-stone-900 truncate">{o.product_title || '—'}</div>
                        <div className="text-xs text-stone-500 truncate">
                          {o.customer_name} &lt;{o.customer_email}&gt;
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <Badge
                          variant="secondary"
                          className={`text-xs ${failed ? 'bg-red-50 text-red-700' : 'bg-stone-100 text-stone-600'}`}
                        >
                          {o.fulfillment_status || '—'}
                        </Badge>
                        <div className="text-right">
                          <div className="text-sm font-semibold text-teal-700">{fmtMoney(o.amount_usd)}</div>
                          {o.gross_margin_usd != null && (
                            <div className="text-[11px] text-stone-500">
                              margin {fmtMoney(o.gross_margin_usd)}
                            </div>
                          )}
                        </div>
                        <span className="text-xs text-stone-500 hidden sm:inline">{fmtDate(o.paid_at)}</span>
                      </div>
                    </div>
                    {failed && (
                      <div className={`mt-2 p-3 rounded-xl border ${isBilling ? 'bg-amber-50 border-amber-200' : 'bg-red-50/60 border-red-100'}`} data-testid={`order-error-${o.id}`}>
                        <div className="flex items-start gap-2">
                          <AlertCircle className={`w-4 h-4 shrink-0 mt-0.5 ${isBilling ? 'text-amber-600' : 'text-red-600'}`} />
                          <div className="flex-1 min-w-0">
                            <div className={`text-xs font-semibold mb-1 ${isBilling ? 'text-amber-900' : 'text-red-800'}`}>
                              {isBilling ? 'Gelato billing setup required' : 'Gelato submission failed'}
                            </div>
                            {isBilling ? (
                              <div className="text-xs text-amber-900 leading-relaxed">
                                Your Gelato billing isn&apos;t complete — they won&apos;t print until you add a payment method.{' '}
                                <a href="https://dashboard.gelato.com/settings/billing" target="_blank" rel="noreferrer" className="font-semibold underline">
                                  Open Gelato billing →
                                </a>{' '}
                                then click <strong>Retry</strong>.
                              </div>
                            ) : (
                              <div className="text-xs text-red-700 break-words">
                                {o.fulfillment_error || 'No error details captured.'}
                              </div>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={retrying === o.id}
                            onClick={() => retryGelato(o.id)}
                            className={`rounded-full text-xs h-8 ${isBilling ? 'border-amber-300 text-amber-800 hover:bg-amber-100' : 'border-red-200 text-red-700 hover:bg-red-100'}`}
                            data-testid={`retry-gelato-${o.id}`}
                          >
                            {retrying === o.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <><RefreshCw className="w-3 h-3 mr-1" /> Retry</>
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-stone-500">No paid print orders in this window.</p>
          )}
        </Card>
      </div>
      <MobileNav />
    </div>
  );
}
